import { useEffect } from "react";
import { z } from "zod";
import { localStore, useStore } from "../../../lib/store";

/** How many collections the history keeps, newest first. */
const MAX_PLAYLIST_VISITS = 10;

const playlistVisitSchema = z.object({
  // Whose visit it is: a shared browser must not show one account's
  // collections to the next one signed in.
  userId: z.string(),
  id: z.string(),
  title: z.string(),
  articleCount: z.number().int(),
  // The room it was opened in, which is where it reopens. `null` outside one.
  roomId: z.string().nullable(),
  visitedAt: z.number(),
});

type PlaylistVisit = z.infer<typeof playlistVisitSchema>;

// Per device and per account: the server counts a collection's views but keeps
// no reader's history of them. The title is kept
// so the desktop search palette can list a visit without fetching it.
const playlistVisitsStore = localStore<PlaylistVisit[]>(
  "playlistVisits",
  [],
  z.array(playlistVisitSchema)
);

function recordPlaylistVisit(playlist: Omit<PlaylistVisit, "visitedAt">): void {
  // Deduplicated and capped per account: one person's browsing never drops
  // another's visits from a shared browser.
  playlistVisitsStore.set((prev) => [
    ...[
      { ...playlist, visitedAt: Date.now() },
      ...prev.filter(
        (visit) => visit.userId === playlist.userId && visit.id !== playlist.id
      ),
    ].slice(0, MAX_PLAYLIST_VISITS),
    ...prev.filter((visit) => visit.userId !== playlist.userId),
  ]);
}

/**
 * Records a visit once both the collection and the account are known,
 * whichever lands first. `null` is "not known yet".
 */
export function useRecordPlaylistVisit(
  playlist: {
    id: string;
    title: string;
    articleCount: number;
    roomId: string | null;
  } | null,
  userId: string | null
): void {
  const id = playlist === null ? null : playlist.id;
  const title = playlist === null ? null : playlist.title;
  const articleCount = playlist === null ? null : playlist.articleCount;
  const roomId = playlist === null ? null : playlist.roomId;
  useEffect(() => {
    if (id === null || title === null || articleCount === null) return;
    if (userId === null) return;
    recordPlaylistVisit({ userId, id, title, articleCount, roomId });
  }, [id, title, articleCount, roomId, userId]);
}

/** The visits `userId` made on this browser, newest first; none signed out. */
export function usePlaylistVisits(
  userId: string | null
): readonly PlaylistVisit[] {
  const [visits] = useStore(playlistVisitsStore);
  if (userId === null) return [];
  return visits.filter((visit) => visit.userId === userId);
}
